import { useState, useEffect } from 'react';
import { PivotTableData } from '../models/PulseData';
import { FetchState } from '../models/commonTypes';
import { CustomerInfo } from '../models/CustomerData';
import { fetchPulseServiceAttributePivotData } from '../services/pulseService';
import { useCustomer } from '../contexts/CustomerContext';
import { TimeSliceWindow } from '@/models/TimeSlice';
import logger from '../utils/logger';

interface UsePulseServiceAttributePivotDataProps {
  selectedCustomer: CustomerInfo | null;
  selectedServicesGroupId: string | null;
  serviceName: string | null;
  attributeName: string | null; // The attribute for which to fetch the pivot
  timeSliceWindow: TimeSliceWindow;  
}

export const usePulseServiceAttributePivotData = ({
  selectedCustomer,
  selectedServicesGroupId,
  serviceName,
  attributeName,
  timeSliceWindow,
}: UsePulseServiceAttributePivotDataProps): FetchState<PivotTableData> => {

  const [pivotTableState, setPivotTableState] = useState<FetchState<PivotTableData>>({
    data: null,
    isLoading: false, // Initially not loading until all params are present
    error: null,
  });

  const { selectedApiKey} = useCustomer();
  
  useEffect(() => {

    logger.debug(`usePulseServiceAttributePivotData called with params: ${selectedServicesGroupId}, ${serviceName}, ${attributeName}, ${timeSliceWindow}`)

    // Only fetch if all necessary parameters are provided
    if (!selectedCustomer || !selectedServicesGroupId || !serviceName || !attributeName) {
      setPivotTableState({ data: null, isLoading: false, error: null }); // Reset if params are missing
      return;
    }

    const fetchAttributePivot = async () => {
      setPivotTableState({ data: null, isLoading: true, error: null });

      try {
        const params = {
          apiKey: selectedApiKey,
          customer: selectedCustomer.id,
          servicesList: [serviceName], // getServicesForGroupId(selectedCustomer, selectedServicesGroupId),
          attribute: attributeName,
          startDate: timeSliceWindow.startTime,
          endDate: timeSliceWindow.endTime,
          timeIncrement: timeSliceWindow.incrementMinutes
        };
        const response = await fetchPulseServiceAttributePivotData(params);

        if (!response || response.error || !response.pivotTable) {
          throw new Error(`Fetching attribute pivot for ${serviceName} - ${attributeName} failed: ${response?.message || 'No data returned'}`);
        }
        setPivotTableState({ data: response.pivotTable, isLoading: false, error: null });
        logger.info(`Successfully fetched pivot for attribute: ${attributeName} in service: ${serviceName}`);
      } catch (err) {
        logger.error(`Failed to fetch pivot for attribute ${attributeName} in service ${serviceName}:`, err);
        const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
        // Consider if fallback to samplePivotData is appropriate here or just show error
        setPivotTableState({ data: null, isLoading: false, error: errorMessage });
      }
    };

    fetchAttributePivot();
  }, [selectedCustomer, selectedApiKey, selectedServicesGroupId, serviceName, attributeName, timeSliceWindow]);

  return pivotTableState;
};